import { useState, useEffect } from "react";
import { API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { 
  Globe, 
  Handshake, 
  TrendingUp, 
  Building2, 
  Factory, 
  Plane, 
  Leaf, 
  Cpu,
  ChevronRight,
  Shield,
  Users,
  Target,
  Zap,
  ArrowRight,
  CheckCircle2,
  Menu,
  X,
  Phone,
  Mail,
  MapPin,
  User
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const HEADLINES = [
  "Connecting Maharashtra to Global Business Opportunities",
  "Where Maharashtra Meets the Global Business World",
  "Maharashtra's Gateway to Trade, Investment & Expansion"
];

const CATEGORIES = [
  { icon: Globe, name: "International Trade & Exports", id: "trade" },
  { icon: Handshake, name: "Strategic Partnerships & Joint Ventures", id: "partnerships" },
  { icon: TrendingUp, name: "Investments & Capital Opportunities", id: "investment" },
  { icon: Building2, name: "Infrastructure & Industrial Projects", id: "infrastructure" },
  { icon: Factory, name: "Manufacturing & MSME Growth", id: "manufacturing" },
  { icon: Plane, name: "Tourism, Hospitality & Lifestyle", id: "tourism" },
  { icon: Leaf, name: "Sustainability & Green Initiatives", id: "sustainability" },
  { icon: Cpu, name: "Technology, Media & Creative Economy", id: "technology" },
];

const WHY_MAHAASETU = [
  { icon: Shield, title: "Curated & Verified", desc: "Quality-assured business opportunities" },
  { icon: Globe, title: "Global Exposure", desc: "Connect with international markets" },
  { icon: Target, title: "Strategic Matchmaking", desc: "Business ↔ Investor ↔ Partner" },
  { icon: Users, title: "Elite Ecosystem", desc: "High-trust, premium network" },
  { icon: Zap, title: "Zero Barriers", desc: "Free for Maharashtra businesses" },
];

const MAHARASHTRA_CITIES = [
  "Mumbai", "Pune", "Nagpur", "Nashik", "Aurangabad", "Thane", "Kolhapur",
  "Solapur", "Amravati", "Nanded", "Sangli", "Jalgaon", "Akola", "Latur",
  "Dhule", "Ahmednagar", "Chandrapur", "Parbhani", "Satara", "Other"
];

const INVESTMENT_RANGES = [
  "Under ₹10 Lakhs",
  "₹10L - ₹50L",
  "₹50L - ₹1 Crore",
  "₹1Cr - ₹10 Crore",
  "₹10Cr - ₹50 Crore",
  "₹50Cr - ₹100 Crore",
  "Above ₹100 Crore"
];

const LandingPage = () => {
  const [headlineIndex, setHeadlineIndex] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showBusinessForm, setShowBusinessForm] = useState(false);
  const [showInvestorForm, setShowInvestorForm] = useState(false);
  const [loading, setLoading] = useState(false);

  // Business Form State
  const [businessForm, setBusinessForm] = useState({
    name: "",
    email: "",
    phone: "",
    company_name: "",
    location: "",
    category: "",
    message: ""
  });

  // Investor Form State
  const [investorForm, setInvestorForm] = useState({
    name: "",
    email: "",
    phone: "",
    company_name: "",
    investment_range: "",
    message: ""
  });

  useEffect(() => {
    const interval = setInterval(() => {
      setHeadlineIndex((prev) => (prev + 1) % HEADLINES.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const handleBusinessSubmit = async (e) => {
    e.preventDefault();
    if (!businessForm.name || !businessForm.email || !businessForm.phone || !businessForm.company_name) {
      toast.error("Please fill in all required fields");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/inquiries`, {
        inquiry_type: "business",
        ...businessForm
      });
      toast.success("Thank you! Our team will contact you shortly.");
      setShowBusinessForm(false);
      setBusinessForm({
        name: "",
        email: "",
        phone: "",
        company_name: "",
        location: "",
        category: "",
        message: ""
      });
    } catch (error) {
      toast.error("Failed to submit. Please try again or call us directly.");
    } finally {
      setLoading(false);
    }
  };

  const handleInvestorSubmit = async (e) => {
    e.preventDefault();
    if (!investorForm.name || !investorForm.email || !investorForm.phone) {
      toast.error("Please fill in all required fields");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/inquiries`, {
        inquiry_type: "investor",
        ...investorForm
      });
      toast.success("Thank you! Our team will contact you shortly.");
      setShowInvestorForm(false);
      setInvestorForm({
        name: "",
        email: "",
        phone: "",
        company_name: "",
        investment_range: "",
        message: ""
      });
    } catch (error) {
      toast.error("Failed to submit. Please try again or call us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center justify-between h-20">
            <a href="#" className="flex items-center gap-3" data-testid="logo-link">
              <span className="text-2xl font-semibold tracking-tight" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                <span className="gold-text">Maha</span>
                <span className="text-white">asetu</span>
              </span>
            </a>

            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-8">
              <a href="#why" className="nav-link text-gray-400 hover:text-white text-sm uppercase tracking-widest transition-colors">Why</a>
              <a href="#sectors" className="nav-link text-gray-400 hover:text-white text-sm uppercase tracking-widest transition-colors">Sectors</a>
              <a href="#investors" className="nav-link text-gray-400 hover:text-white text-sm uppercase tracking-widest transition-colors">Investors</a>
              <a href="#businesses" className="nav-link text-gray-400 hover:text-white text-sm uppercase tracking-widest transition-colors">Businesses</a>
              <a href="#contact" className="nav-link text-gray-400 hover:text-white text-sm uppercase tracking-widest transition-colors">Contact</a>
            </div>

            <div className="hidden md:flex items-center gap-4">
              <button 
                onClick={() => setShowBusinessForm(true)}
                className="btn-primary"
                data-testid="nav-get-started-btn"
              >
                Get Started
              </button>
            </div>

            {/* Mobile menu button */}
            <button 
              className="md:hidden text-white"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-[#0A0A0A] border-t border-white/5">
            <div className="px-6 py-4 space-y-4">
              <a href="#why" className="block text-gray-400 hover:text-white text-sm uppercase tracking-widest">Why</a>
              <a href="#sectors" className="block text-gray-400 hover:text-white text-sm uppercase tracking-widest">Sectors</a>
              <a href="#investors" className="block text-gray-400 hover:text-white text-sm uppercase tracking-widest">Investors</a>
              <a href="#businesses" className="block text-gray-400 hover:text-white text-sm uppercase tracking-widest">Businesses</a>
              <a href="#contact" className="block text-gray-400 hover:text-white text-sm uppercase tracking-widest">Contact</a>
              <div className="pt-4 border-t border-white/10">
                <button onClick={() => { setShowBusinessForm(true); setMobileMenuOpen(false); }} className="btn-primary block w-full text-center">
                  Get Started
                </button>
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ 
            backgroundImage: `url('https://images.unsplash.com/photo-1759770384425-705fa1fe342f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwyfHxCYW5kcmElMjBXb3JsaSUyMFNlYSUyMExpbmslMjBuaWdodCUyMGJyaWRnZXxlbnwwfHx8fDE3NzQ0NTAwODJ8MA&ixlib=rb-4.1.0&q=85')`
          }}
        />
        <div className="absolute inset-0 hero-overlay" />
        
        {/* Grid Pattern */}
        <div className="absolute inset-0 grid-pattern opacity-30" />

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pt-32 pb-20">
          <div className="max-w-4xl">
            {/* Free Badge */}
            <div className="inline-flex items-center gap-2 mb-8 animate-fadeInUp">
              <span className="badge-free flex items-center gap-2">
                <CheckCircle2 size={12} />
                FREE Registration for Maharashtra Businesses
              </span>
            </div>

            {/* Headline */}
            <h1 
              className="text-5xl md:text-7xl lg:text-8xl tracking-tight leading-[0.9] mb-8 animate-fadeInUp animation-delay-200"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
              data-testid="hero-headline"
            >
              <span className="text-white">{HEADLINES[headlineIndex].split(' ').slice(0, -2).join(' ')} </span>
              <span className="gold-text">{HEADLINES[headlineIndex].split(' ').slice(-2).join(' ')}</span>
            </h1>

            {/* Subheadline */}
            <p className="text-lg md:text-xl text-gray-300 leading-relaxed max-w-2xl mb-12 animate-fadeInUp animation-delay-400">
              A trusted platform enabling businesses, investors, and enterprises to discover, connect, and collaborate on high-value opportunities across Maharashtra.
            </p>

            {/* CTA */}
            <div className="flex flex-col sm:flex-row gap-4 animate-fadeInUp animation-delay-600">
              <button 
                onClick={() => setShowBusinessForm(true)}
                className="btn-primary flex items-center justify-center gap-2 pulse-gold"
                data-testid="hero-register-btn"
              >
                Register Your Business – Free
                <ArrowRight size={16} />
              </button>
            </div>

            {/* Micro copy */}
            <p className="text-sm text-gray-500 mt-8 uppercase tracking-widest animate-fadeInUp animation-delay-800">
              Powering Maharashtra. Connecting the World.
            </p>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border border-white/20 rounded-full flex justify-center pt-2">
            <div className="w-1 h-2 bg-[#D4AF37] rounded-full" />
          </div>
        </div>
      </section>

      {/* Why Mahaasetu Section */}
      <section id="why" className="py-24 md:py-32 relative">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="mb-16">
            <p className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] mb-4">Why Choose Us</p>
            <h2 
              className="text-3xl md:text-5xl tracking-tight text-white"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              Why <span className="gold-text">Mahaasetu</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
            {/* Large feature card */}
            <div className="md:col-span-8 feature-card p-8 md:p-12">
              <div className="flex items-start gap-6">
                <div className="w-16 h-16 bg-[#D4AF37]/10 flex items-center justify-center flex-shrink-0">
                  <Globe className="w-8 h-8 text-[#D4AF37]" />
                </div>
                <div>
                  <h3 className="text-2xl md:text-3xl text-white mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                    Maharashtra's Bridge to Global Business
                  </h3>
                  <p className="text-gray-400 leading-relaxed">
                    Mahaasetu is not a directory. It is a premium gateway connecting Maharashtra's business potential 
                    with national and international opportunities. Our platform enables strategic matchmaking between 
                    businesses, investors, and partners.
                  </p>
                </div>
              </div>
            </div>

            {/* Small feature cards */}
            <div className="md:col-span-4 space-y-6">
              {WHY_MAHAASETU.slice(0, 2).map((item, index) => (
                <div key={index} className="obsidian-card p-6">
                  <item.icon className="w-6 h-6 text-[#D4AF37] mb-4" />
                  <h4 className="text-white font-semibold mb-2">{item.title}</h4>
                  <p className="text-gray-500 text-sm">{item.desc}</p>
                </div>
              ))}
            </div>

            {/* Bottom row */}
            {WHY_MAHAASETU.slice(2).map((item, index) => (
              <div key={index} className="md:col-span-4 obsidian-card p-6">
                <item.icon className="w-6 h-6 text-[#D4AF37] mb-4" />
                <h4 className="text-white font-semibold mb-2">{item.title}</h4>
                <p className="text-gray-500 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Business Sectors */}
      <section id="sectors" className="py-24 md:py-32 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="mb-16">
            <p className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] mb-4">Explore Sectors</p>
            <h2 
              className="text-3xl md:text-5xl tracking-tight text-white"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              Business Opportunity <span className="gold-text">Exchange</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {CATEGORIES.map((category, index) => (
              <div
                key={category.id}
                className="category-card obsidian-card p-6 group cursor-pointer"
                onClick={() => setShowBusinessForm(true)}
                data-testid={`category-${category.id}`}
              >
                <category.icon className="w-8 h-8 text-[#D4AF37] mb-4 group-hover:scale-110 transition-transform" />
                <h3 className="text-white font-medium mb-2">{category.name}</h3>
                <div className="flex items-center gap-2 text-gray-500 text-sm group-hover:text-[#D4AF37] transition-colors">
                  <span>Enquire</span>
                  <ChevronRight size={14} className="group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* For Global Investors */}
      <section id="investors" className="py-24 md:py-32 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img 
            src="https://images.unsplash.com/photo-1758745791998-11ea5eb5df40?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwxfHxDb250YWluZXIlMjBzaGlwJTIwcG9ydCUyMHRlcm1pbmFsJTIwbmlnaHR8ZW58MHx8fHwxNzc0NDUwMDkzfDA&ixlib=rb-4.1.0&q=85"
            alt="Global Trade"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-[#050505]/95 to-transparent" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-2xl">
            <p className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] mb-4">For Global Investors</p>
            <h2 
              className="text-3xl md:text-5xl tracking-tight text-white mb-6"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              Access High-Growth Opportunities Across <span className="gold-text">Maharashtra</span>
            </h2>
            
            <ul className="space-y-4 mb-8">
              <li className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                <span className="text-gray-300">Verified & structured opportunities</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                <span className="text-gray-300">Direct access to Maharashtra's business ecosystem</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                <span className="text-gray-300">One platform for discovery, validation & deal flow</span>
              </li>
            </ul>

            <button 
              onClick={() => setShowInvestorForm(true)}
              className="btn-primary inline-flex items-center gap-2"
              data-testid="investor-get-started-btn"
            >
              Get Started
              <ArrowRight size={16} />
            </button>
          </div>
        </div>
      </section>

      {/* For Maharashtra Businesses */}
      <section id="businesses" className="py-24 md:py-32 bg-[#0A0A0A] relative overflow-hidden">
        <div className="absolute top-0 right-0 w-1/2 h-full opacity-20">
          <img 
            src="https://images.unsplash.com/photo-1769271972348-429a8ad18f51?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwzfHxNdW1iYWklMjBjaXR5JTIwc2t5bGluZSUyMG5pZ2h0JTIwZmluYW5jaWFsJTIwZGlzdHJpY3R8ZW58MHx8fHwxNzc0NDUwMDk2fDA&ixlib=rb-4.1.0&q=85"
            alt="Mumbai Skyline"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-l from-[#0A0A0A] via-[#0A0A0A]/95 to-transparent" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-2xl ml-auto text-right">
            <div className="inline-flex items-center gap-2 mb-4">
              <span className="badge-free">100% Free Registration</span>
            </div>
            <p className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] mb-4">For Maharashtra Businesses</p>
            <h2 
              className="text-3xl md:text-5xl tracking-tight text-white mb-6"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              Take Your Maharashtra Business to the <span className="gold-text">Global Stage</span>
            </h2>
            
            <ul className="space-y-4 mb-8">
              <li className="flex items-start gap-3 justify-end">
                <span className="text-gray-300">FREE registration on Mahaasetu</span>
                <CheckCircle2 className="w-5 h-5 text-[#10B981] flex-shrink-0 mt-0.5" />
              </li>
              <li className="flex items-start gap-3 justify-end">
                <span className="text-gray-300">Visibility to global investors & partners</span>
                <CheckCircle2 className="w-5 h-5 text-[#10B981] flex-shrink-0 mt-0.5" />
              </li>
              <li className="flex items-start gap-3 justify-end">
                <span className="text-gray-300">Expand beyond Maharashtra into international markets</span>
                <CheckCircle2 className="w-5 h-5 text-[#10B981] flex-shrink-0 mt-0.5" />
              </li>
              <li className="flex items-start gap-3 justify-end">
                <span className="text-gray-300">Zero entry barriers</span>
                <CheckCircle2 className="w-5 h-5 text-[#10B981] flex-shrink-0 mt-0.5" />
              </li>
            </ul>

            <button 
              onClick={() => setShowBusinessForm(true)}
              className="btn-primary inline-flex items-center gap-2"
              data-testid="business-register-btn"
            >
              Register Your Business – Free
              <ArrowRight size={16} />
            </button>
          </div>
        </div>
      </section>

      {/* Deal Flow Section */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] mb-4">Deal Flow Engine</p>
            <h2 
              className="text-3xl md:text-5xl tracking-tight text-white mb-4"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              Deal Flow & <span className="gold-text">Matchmaking</span>
            </h2>
            <p className="text-gray-400 max-w-xl mx-auto">
              The right connection creates exponential growth.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Shield, title: "Opportunity Screening", desc: "Quality-first validation process" },
              { icon: Target, title: "Smart Matchmaking", desc: "Strategic business matching" },
              { icon: Handshake, title: "Confidential Deals", desc: "Secure deal facilitation" },
              { icon: TrendingUp, title: "Quality Ecosystem", desc: "Premium business network" },
            ].map((item, index) => (
              <div key={index} className="obsidian-card p-8 text-center">
                <div className="w-16 h-16 bg-[#D4AF37]/10 flex items-center justify-center mx-auto mb-6">
                  <item.icon className="w-8 h-8 text-[#D4AF37]" />
                </div>
                <h3 className="text-white font-semibold mb-2">{item.title}</h3>
                <p className="text-gray-500 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-24 md:py-32 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] mb-4">Trust & Credibility</p>
            <h2 
              className="text-3xl md:text-5xl tracking-tight text-white"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              Built on <span className="gold-text">Trust</span>
            </h2>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { label: "Verified Listings Only", icon: Shield },
              { label: "Professional Standards", icon: Users },
              { label: "Structured Framework", icon: Target },
              { label: "Maharashtra-Focused", icon: Globe },
            ].map((item, index) => (
              <div key={index} className="text-center">
                <div className="w-12 h-12 border border-white/10 flex items-center justify-center mx-auto mb-4">
                  <item.icon className="w-6 h-6 text-gray-400" />
                </div>
                <p className="text-gray-400 text-sm">{item.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* User Paths */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] mb-4">Your Path</p>
            <h2 
              className="text-3xl md:text-5xl tracking-tight text-white"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              Choose Your <span className="gold-text">Journey</span>
            </h2>
          </div>

          <div className="flex flex-wrap justify-center gap-4">
            {["Invest", "Partner", "Trade", "Expand", "Collaborate"].map((path) => (
              <button
                key={path}
                onClick={() => path === "Invest" ? setShowInvestorForm(true) : setShowBusinessForm(true)}
                className="px-8 py-4 border border-white/10 text-gray-400 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-all text-sm uppercase tracking-widest"
                data-testid={`path-${path.toLowerCase()}`}
              >
                {path}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 md:py-32 bg-[#0A0A0A] relative overflow-hidden">
        <div className="absolute inset-0 glow" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 gap-12 md:gap-24">
            {/* For Maharashtra Businesses */}
            <div className="feature-card p-8 md:p-12">
              <span className="badge-free mb-6 inline-block">Free Registration</span>
              <h3 
                className="text-2xl md:text-3xl text-white mb-4"
                style={{ fontFamily: 'Cormorant Garamond, serif' }}
              >
                Are You a Maharashtra-Based Business or Service Provider?
              </h3>
              <p className="text-gray-400 mb-8">
                Register for FREE and unlock global business opportunities.
              </p>
              <button 
                onClick={() => setShowBusinessForm(true)}
                className="btn-primary inline-flex items-center gap-2"
                data-testid="final-register-business"
              >
                Register Free
                <ArrowRight size={16} />
              </button>
            </div>

            {/* For Investors */}
            <div className="obsidian-card p-8 md:p-12">
              <span className="badge-gold mb-6 inline-block">Premium Access</span>
              <h3 
                className="text-2xl md:text-3xl text-white mb-4"
                style={{ fontFamily: 'Cormorant Garamond, serif' }}
              >
                Looking to Invest, Partner or Expand in Maharashtra?
              </h3>
              <p className="text-gray-400 mb-8">
                Get started with Mahaasetu and discover high-value opportunities.
              </p>
              <button 
                onClick={() => setShowInvestorForm(true)}
                className="btn-secondary inline-flex items-center gap-2"
                data-testid="final-register-investor"
              >
                Get Started
                <ArrowRight size={16} />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] mb-4">Get In Touch</p>
            <h2 
              className="text-3xl md:text-5xl tracking-tight text-white mb-4"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              Contact <span className="gold-text">Us</span>
            </h2>
            <p className="text-gray-400 max-w-xl mx-auto">
              Have questions? Our team is here to help you connect with the right opportunities.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-2xl mx-auto">
            <a 
              href="mailto:enquiry@mahaasetu.com" 
              className="obsidian-card p-8 text-center group hover:border-[#D4AF37]/30 transition-all"
              data-testid="contact-email"
            >
              <div className="w-16 h-16 bg-[#D4AF37]/10 flex items-center justify-center mx-auto mb-6 group-hover:bg-[#D4AF37]/20 transition-colors">
                <Mail className="w-8 h-8 text-[#D4AF37]" />
              </div>
              <h3 className="text-white font-semibold mb-2">Email Us</h3>
              <p className="text-[#D4AF37]">enquiry@mahaasetu.com</p>
            </a>

            <a 
              href="tel:+919076050999" 
              className="obsidian-card p-8 text-center group hover:border-[#D4AF37]/30 transition-all"
              data-testid="contact-phone"
            >
              <div className="w-16 h-16 bg-[#D4AF37]/10 flex items-center justify-center mx-auto mb-6 group-hover:bg-[#D4AF37]/20 transition-colors">
                <Phone className="w-8 h-8 text-[#D4AF37]" />
              </div>
              <h3 className="text-white font-semibold mb-2">Call Us</h3>
              <p className="text-[#D4AF37]">+91 9076050999</p>
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-16 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row justify-between items-center gap-8">
            <div>
              <span className="text-2xl font-semibold tracking-tight" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                <span className="gold-text">Maha</span>
                <span className="text-white">asetu</span>
              </span>
              <p className="text-gray-500 text-sm mt-2">
                Maharashtra's Gateway to Global Business
              </p>
            </div>
            
            <div className="flex flex-col md:flex-row items-center gap-6 md:gap-8">
              <a href="mailto:enquiry@mahaasetu.com" className="text-gray-400 hover:text-[#D4AF37] text-sm flex items-center gap-2 transition-colors">
                <Mail size={16} />
                enquiry@mahaasetu.com
              </a>
              <a href="tel:+919076050999" className="text-gray-400 hover:text-[#D4AF37] text-sm flex items-center gap-2 transition-colors">
                <Phone size={16} />
                +91 9076050999
              </a>
            </div>
          </div>
          
          <div className="mt-12 pt-8 border-t border-white/5 text-center">
            <p className="text-gray-600 text-sm">
              "Mahaasetu is not a directory. It is Maharashtra's bridge to global business."
            </p>
            <p className="text-gray-700 text-xs mt-4">
              © {new Date().getFullYear()} Mahaasetu. All rights reserved.
            </p>
            <p className="text-gray-600 text-sm mt-4 uppercase tracking-widest">
              An Initiative by <span className="text-[#D4AF37]">The Visibility</span>
            </p>
          </div>
        </div>
      </footer>

      {/* Business Registration Form Modal */}
      <Dialog open={showBusinessForm} onOpenChange={setShowBusinessForm}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              Register Your <span className="gold-text">Maharashtra Business</span>
            </DialogTitle>
            <p className="text-gray-400 text-sm mt-2">100% Free for Maharashtra-based businesses</p>
          </DialogHeader>
          
          <form onSubmit={handleBusinessSubmit} className="space-y-5 mt-4">
            <div>
              <label className="block text-gray-400 text-sm mb-2">Full Name *</label>
              <div className="relative">
                <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-600" />
                <input
                  type="text"
                  value={businessForm.name}
                  onChange={(e) => setBusinessForm({ ...businessForm, name: e.target.value })}
                  className="w-full bg-black/50 border border-white/10 text-white pl-12 pr-4 py-3 focus:border-[#D4AF37] outline-none"
                  placeholder="Your full name"
                  data-testid="business-form-name"
                />
              </div>
            </div>

            <div>
              <label className="block text-gray-400 text-sm mb-2">Email *</label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-600" />
                <input
                  type="email"
                  value={businessForm.email}
                  onChange={(e) => setBusinessForm({ ...businessForm, email: e.target.value })}
                  className="w-full bg-black/50 border border-white/10 text-white pl-12 pr-4 py-3 focus:border-[#D4AF37] outline-none"
                  placeholder="your@email.com"
                  data-testid="business-form-email"
                />
              </div>
            </div>

            <div>
              <label className="block text-gray-400 text-sm mb-2">Phone *</label>
              <div className="relative">
                <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-600" />
                <input
                  type="tel"
                  value={businessForm.phone}
                  onChange={(e) => setBusinessForm({ ...businessForm, phone: e.target.value })}
                  className="w-full bg-black/50 border border-white/10 text-white pl-12 pr-4 py-3 focus:border-[#D4AF37] outline-none"
                  placeholder="+91 XXXXX XXXXX"
                  data-testid="business-form-phone"
                />
              </div>
            </div>

            <div>
              <label className="block text-gray-400 text-sm mb-2">Business Name *</label>
              <div className="relative">
                <Building2 className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-600" />
                <input
                  type="text"
                  value={businessForm.company_name}
                  onChange={(e) => setBusinessForm({ ...businessForm, company_name: e.target.value })}
                  className="w-full bg-black/50 border border-white/10 text-white pl-12 pr-4 py-3 focus:border-[#D4AF37] outline-none"
                  placeholder="Your business name"
                  data-testid="business-form-company"
                />
              </div>
            </div>

            <div>
              <label className="block text-gray-400 text-sm mb-2">Location in Maharashtra</label>
              <Select 
                value={businessForm.location} 
                onValueChange={(value) => setBusinessForm({ ...businessForm, location: value })}
              >
                <SelectTrigger className="w-full bg-black/50 border-white/10 text-white" data-testid="business-form-location">
                  <MapPin className="w-4 h-4 mr-2 text-gray-500" />
                  <SelectValue placeholder="Select city" />
                </SelectTrigger>
                <SelectContent className="bg-[#0A0A0A] border-white/10 max-h-[200px]">
                  {MAHARASHTRA_CITIES.map((city) => (
                    <SelectItem key={city} value={city} className="text-white hover:bg-white/5">
                      {city}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="block text-gray-400 text-sm mb-2">Business Category</label>
              <Select 
                value={businessForm.category} 
                onValueChange={(value) => setBusinessForm({ ...businessForm, category: value })}
              >
                <SelectTrigger className="w-full bg-black/50 border-white/10 text-white" data-testid="business-form-category">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent className="bg-[#0A0A0A] border-white/10">
                  {CATEGORIES.map((cat) => (
                    <SelectItem key={cat.id} value={cat.id} className="text-white hover:bg-white/5">
                      {cat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="block text-gray-400 text-sm mb-2">Message (Optional)</label>
              <textarea
                value={businessForm.message}
                onChange={(e) => setBusinessForm({ ...businessForm, message: e.target.value })}
                className="w-full bg-black/50 border border-white/10 text-white px-4 py-3 focus:border-[#D4AF37] outline-none min-h-[100px] resize-none"
                placeholder="Tell us about your business and what you're looking for..."
                data-testid="business-form-message"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full flex items-center justify-center gap-2"
              data-testid="business-form-submit"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  Submit Registration
                  <ArrowRight size={16} />
                </>
              )}
            </button>

            <p className="text-gray-600 text-xs text-center">
              Our team will contact you within 24-48 hours
            </p>
          </form>
        </DialogContent>
      </Dialog>

      {/* Investor Form Modal */}
      <Dialog open={showInvestorForm} onOpenChange={setShowInvestorForm}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              Investor / Partner <span className="gold-text">Inquiry</span>
            </DialogTitle>
            <p className="text-gray-400 text-sm mt-2">Access premium opportunities in Maharashtra</p>
          </DialogHeader>
          
          <form onSubmit={handleInvestorSubmit} className="space-y-5 mt-4">
            <div>
              <label className="block text-gray-400 text-sm mb-2">Full Name *</label>
              <div className="relative">
                <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-600" />
                <input
                  type="text"
                  value={investorForm.name}
                  onChange={(e) => setInvestorForm({ ...investorForm, name: e.target.value })}
                  className="w-full bg-black/50 border border-white/10 text-white pl-12 pr-4 py-3 focus:border-[#D4AF37] outline-none"
                  placeholder="Your full name"
                  data-testid="investor-form-name"
                />
              </div>
            </div>

            <div>
              <label className="block text-gray-400 text-sm mb-2">Email *</label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-600" />
                <input
                  type="email"
                  value={investorForm.email}
                  onChange={(e) => setInvestorForm({ ...investorForm, email: e.target.value })}
                  className="w-full bg-black/50 border border-white/10 text-white pl-12 pr-4 py-3 focus:border-[#D4AF37] outline-none"
                  placeholder="your@email.com"
                  data-testid="investor-form-email"
                />
              </div>
            </div>

            <div>
              <label className="block text-gray-400 text-sm mb-2">Phone *</label>
              <div className="relative">
                <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-600" />
                <input
                  type="tel"
                  value={investorForm.phone}
                  onChange={(e) => setInvestorForm({ ...investorForm, phone: e.target.value })}
                  className="w-full bg-black/50 border border-white/10 text-white pl-12 pr-4 py-3 focus:border-[#D4AF37] outline-none"
                  placeholder="+91 XXXXX XXXXX"
                  data-testid="investor-form-phone"
                />
              </div>
            </div>

            <div>
              <label className="block text-gray-400 text-sm mb-2">Company / Organization</label>
              <div className="relative">
                <Building2 className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-600" />
                <input
                  type="text"
                  value={investorForm.company_name}
                  onChange={(e) => setInvestorForm({ ...investorForm, company_name: e.target.value })}
                  className="w-full bg-black/50 border border-white/10 text-white pl-12 pr-4 py-3 focus:border-[#D4AF37] outline-none"
                  placeholder="Your company name"
                  data-testid="investor-form-company"
                />
              </div>
            </div>

            <div>
              <label className="block text-gray-400 text-sm mb-2">Investment Range</label>
              <Select 
                value={investorForm.investment_range} 
                onValueChange={(value) => setInvestorForm({ ...investorForm, investment_range: value })}
              >
                <SelectTrigger className="w-full bg-black/50 border-white/10 text-white" data-testid="investor-form-range">
                  <TrendingUp className="w-4 h-4 mr-2 text-gray-500" />
                  <SelectValue placeholder="Select range" />
                </SelectTrigger>
                <SelectContent className="bg-[#0A0A0A] border-white/10">
                  {INVESTMENT_RANGES.map((range) => (
                    <SelectItem key={range} value={range} className="text-white hover:bg-white/5">
                      {range}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="block text-gray-400 text-sm mb-2">Message (Optional)</label>
              <textarea
                value={investorForm.message}
                onChange={(e) => setInvestorForm({ ...investorForm, message: e.target.value })}
                className="w-full bg-black/50 border border-white/10 text-white px-4 py-3 focus:border-[#D4AF37] outline-none min-h-[100px] resize-none"
                placeholder="Tell us about your investment interests and what you're looking for..."
                data-testid="investor-form-message"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full flex items-center justify-center gap-2"
              data-testid="investor-form-submit"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  Submit Inquiry
                  <ArrowRight size={16} />
                </>
              )}
            </button>

            <p className="text-gray-600 text-xs text-center">
              Our team will contact you within 24-48 hours
            </p>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LandingPage;
